import type { RouteRecordRaw, Router } from 'vue-router'
import { useAuthStore } from '@/modules/Auth/store'
import { MODULE_PERMISSIONS } from '@/modules/Core/constants/adminAccess'
import { HrPermission } from '@/modules/Core/constants/hrPermissions'
import { canAccessPermissions } from '@/modules/Core/utils/permissions'

const evaluationModulePermissions = [
  HrPermission.FormReadAll,
  HrPermission.FormReadEvaluation,
  HrPermission.FormCreate,
  HrPermission.FormUpdate,
  HrPermission.FormSubmissionCreate,
  HrPermission.FormSubmissionReadOwn,
  HrPermission.FormSubmissionReadAll,
] as const

function resolveEvaluationModuleRedirect() {
  const authStore = useAuthStore()

  if (
    canAccessPermissions(
      authStore.permissions,
      MODULE_PERMISSIONS.evaluation,
      'any'
    )
  ) {
    return { name: 'ManageEvaluationsRoute' }
  }

  if (
    canAccessPermissions(
      authStore.permissions,
      MODULE_PERMISSIONS.evaluationAssigned,
      'any'
    )
  ) {
    return { name: 'EvaluationAssignedRoute' }
  }

  if (
    canAccessPermissions(
      authStore.permissions,
      MODULE_PERMISSIONS.evaluationMyPerformance,
      'any'
    )
  ) {
    return { name: 'EvaluationMyPerformanceRoute' }
  }

  return { name: 'home' }
}

const EvaluationRoutes: RouteRecordRaw = {
  path: '/evaluation',
  name: 'EvaluationModulePages',
  redirect: resolveEvaluationModuleRedirect,
  children: [
    {
      path: '',
      name: 'ManageEvaluationsRoute',
      component: () => import('./views/ManageEvaluationsView.vue'),
      meta: {
        title: 'ایجاد ارزشیابی',
        requiredPermissions: [...MODULE_PERMISSIONS.evaluation],
      },
    },
    {
      path: 'assigned',
      name: 'EvaluationAssignedRoute',
      component: () => import('./views/EvaluationAssignedView.vue'),
      meta: {
        title: 'ارزشیابی‌ها',
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationAssigned],
        permissionMode: 'any',
      },
    },
    {
      path: 'assigned/:formId/fill',
      name: 'EvaluationAssignedFillRoute',
      component: () => import('./views/EvaluationAssignedFillView.vue'),
      meta: {
        title: 'انجام ارزشیابی',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationAssigned],
        permissionMode: 'any',
      },
    },
    {
      path: 'assigned/:formId/statistics',
      name: 'EvaluationAssignedStatisticsRoute',
      component: () => import('./views/EvaluationAssignedStatisticsView.vue'),
      meta: {
        title: 'عملکرد من',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationAssigned],
        permissionMode: 'any',
      },
    },
    {
      path: 'assigned/:formId/submissions',
      name: 'EvaluationAssignedSubmissionsRoute',
      component: () => import('./views/EvaluationAssignedSubmissionsView.vue'),
      meta: {
        title: 'لیست ارزشیابی‌های ثبت‌شده',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationAssigned],
        permissionMode: 'any',
      },
    },
    {
      path: 'my-performance',
      name: 'EvaluationMyPerformanceRoute',
      component: () => import('./views/EvaluationMyPerformanceView.vue'),
      meta: {
        title: 'عملکرد من',
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationMyPerformance],
      },
    },
    {
      path: 'form-builder/create',
      name: 'EvaluationFormBuilderCreateRoute',
      component: () => import('./views/EvaluationFormBuilderView.vue'),
      meta: {
        title: 'فرم ساز',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationFormCreate],
      },
    },
    {
      path: 'form-builder/create/preview',
      name: 'EvaluationFormBuilderCreatePreviewRoute',
      component: () =>
        import('@/modules/Recruitment/views/FormBuilderPreviewView.vue'),
      meta: {
        title: 'پیش‌نمایش فرم',
        pageHasBack: true,
        previewFallback: 'ManageEvaluationsRoute',
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationFormCreate],
      },
    },
    {
      path: ':id/form-builder',
      name: 'EvaluationFormBuilderRoute',
      component: () => import('./views/EvaluationFormBuilderView.vue'),
      meta: {
        title: 'فرم ساز',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationFormEdit],
      },
    },
    {
      path: ':id/form-builder/preview',
      name: 'EvaluationFormBuilderPreviewRoute',
      component: () =>
        import('@/modules/Recruitment/views/FormBuilderPreviewView.vue'),
      meta: {
        title: 'پیش‌نمایش فرم',
        pageHasBack: true,
        previewFallback: 'ManageEvaluationsRoute',
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationFormEdit],
      },
    },
    {
      path: ':id/submissions/:submissionId',
      name: 'EvaluationSubmissionResultRoute',
      component: () => import('./views/EvaluationSubmissionResultView.vue'),
      meta: {
        title: 'نتیجه ارزشیابی',
        pageHasBack: true,
        requiredPermissions: [
          ...MODULE_PERMISSIONS.evaluationSubmissions,
          ...MODULE_PERMISSIONS.evaluationMyPerformance,
        ],
        permissionMode: 'any',
      },
    },
    {
      path: ':id/submissions',
      name: 'EvaluationSubmissionsRoute',
      component: () => import('./views/EvaluationSubmissionsView.vue'),
      meta: {
        title: 'ارزشیابی‌های ثبت‌شده',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluationSubmissions],
      },
    },
    {
      path: ':id',
      name: 'EvaluationFormRoute',
      component: () => import('./views/EvaluationFormView.vue'),
      meta: {
        title: 'فرم ارزشیابی',
        pageHasBack: true,
        requiredPermissions: [...MODULE_PERMISSIONS.evaluation],
      },
    },
  ],
  meta: {
    layout: 'Default',
    requiredPermissions: [...evaluationModulePermissions],
    permissionMode: 'any',
  },
}

export default (router: Router) => {
  router.addRoute(EvaluationRoutes)
}
